import { createSlice } from "@reduxjs/toolkit";
import { MasterQuantityAddAPIs } from "service/apiVariables";
import { api } from "service/api";
import { Toast } from "service/toast";
import { setLoader } from "./loader";
import { addQuery } from "helpers/utis";

const initialState = {
  wings: [],
  floors: [],
  units: [],
  workLocations: [],
  finalDetails: [],
  singleProjectData: "",
  selectedTradeId: null,
  selectedWorkType: null,
  selectedWingId: null,
  selectedFloorId: null,
  selectedUnitId: null,
  selectedWorkLocationId: null,
  wingChangeKey: 0,
  floorChangeKey: 0,
  unitChangeKey: 0,
  loading: {
    project: false,
    wings: false,
    floors: false,
    units: false,
    workLocations: false,
    finalDetails: false,
    updateQuantity: false,
  },

  error: null,
};

const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data_list)) return data.data_list;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const projectExplorerSlice = createSlice({
  name: "projectExplorer",
  initialState,
  reducers: {
    saveWings: (state, action) => {
      state.wings = action.payload || [];
    },

    saveFloors: (state, action) => {
      state.floors = action.payload || [];
    },

    saveUnits: (state, action) => {
      state.units = action.payload || [];
    },

    saveWorkLocations: (state, action) => {
      state.workLocations = action.payload || [];
    },

    saveFinalDetails: (state, action) => {
      state.finalDetails = action.payload || [];
    },

    saveProjectDetail: (state, action) => {
      state.singleProjectData = action.payload || "";
    },

    updateFinalDetailsItemInState: (state, action) => {
      const updatedItem = action.payload;

      state.finalDetails = (state.finalDetails || []).map((item) =>
        Number(item.master_quantity_id) ===
        Number(updatedItem.master_quantity_id)
          ? { ...item, ...updatedItem }
          : item,
      );
    },

    selectWorkType: (state, action) => {
      state.selectedWorkType = action.payload;
      state.selectedWingId = null;
      state.selectedFloorId = null;
      state.selectedUnitId = null;
      state.selectedWorkLocationId = null;
      state.selectedTradeId = null;

      state.wings = [];
      state.floors = [];
      state.units = [];
      state.workLocations = [];
      state.finalDetails = [];
      state.error = null;
    },

    selectWing: (state, action) => {
      state.selectedWingId = action.payload;
      state.selectedFloorId = null;
      state.selectedUnitId = null;
      state.selectedWorkLocationId = null;
      state.selectedTradeId = null;

      state.floors = [];
      state.units = [];
      state.finalDetails = [];
      state.workLocations = [];
      state.wingChangeKey += 1;
    },

    selectFloor: (state, action) => {
      state.selectedFloorId = action.payload;
      state.selectedUnitId = null;
      state.selectedWorkLocationId = null;
      state.selectedTradeId = null;
      state.units = [];
      state.finalDetails = [];
      state.floorChangeKey += 1;
    },

    selectUnit: (state, action) => {
      state.selectedUnitId = action.payload;
      state.selectedWorkLocationId = null;
      state.selectedTradeId = null;
      state.finalDetails = [];
      state.unitChangeKey += 1;
    },

    selectWorkLocation: (state, action) => {
      state.selectedWorkLocationId = action.payload;
      state.selectedTradeId = null;
      state.finalDetails = [];
    },

    selectTrade: (state, action) => {
      state.selectedTradeId = action.payload;
      state.finalDetails = [];
    },

    setSectionLoading: (state, action) => {
      const { key, value } = action.payload;
      state.loading[key] = value;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    clearProjectBrowser: () => ({ ...initialState }),
  },
});

export const {
  saveWings,
  saveFloors,
  saveUnits,
  saveWorkLocations,
  saveFinalDetails,
  saveProjectDetail,
  updateFinalDetailsItemInState,
  selectWorkType,
  selectTrade,
  selectWing,
  selectFloor,
  selectUnit,
  selectWorkLocation,
  setSectionLoading,
  setError,
  clearProjectBrowser,
} = projectExplorerSlice.actions;

export default projectExplorerSlice.reducer;

/* ---------------- THUNKS ---------------- */

export const getSingleProjectAct =
  (projectId, location_type) => async (dispatch) => {
    try {
      dispatch(setError(null));
      dispatch(setSectionLoading({ key: "project", value: true }));
      dispatch(setLoader(true));

      const baseConfig = MasterQuantityAddAPIs.getSingleProjectApi;
      const apiConfig = {
        method: baseConfig.method,
        baseURL: baseConfig.baseURL,
        api: baseConfig.api(projectId, location_type),
      };

      const { data } = await api(apiConfig);

      dispatch(saveProjectDetail(data));
      return data;
    } catch (error) {
      const message = getErrorMessage(error, "Failed to load project details");

      dispatch(setError(message));
      Toast({ type: "error", message });
      throw error;
    } finally {
      dispatch(setSectionLoading({ key: "project", value: false }));
      dispatch(setLoader(false));
    }
  };

export const fetchWings = (query) => async (dispatch) => {
  try {
    dispatch(setError(null));
    dispatch(setSectionLoading({ key: "wings", value: true }));
    dispatch(setLoader(true));

    const baseConfig = MasterQuantityAddAPIs.getMasterQuantityWingsApi;
    const apiConfig = {
      method: baseConfig.method,
      baseURL: baseConfig.baseURL,
      api: baseConfig.api(query.project_id, query.worklocationtype),
    };

    const { data } = await api(apiConfig);
    dispatch(saveWings(extractList(data)));
    return data;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to load wings");
    dispatch(setError(message));
    Toast({ type: "error", message });
    throw error;
  } finally {
    dispatch(setSectionLoading({ key: "wings", value: false }));
    dispatch(setLoader(false));
  }
};

export const fetchFloors = (query) => async (dispatch) => {
  try {
    dispatch(setError(null));
    dispatch(setSectionLoading({ key: "floors", value: true }));
    dispatch(setLoader(true));

    const baseConfig =
      MasterQuantityAddAPIs.getMasterQuantityWingsBasedFloorApi;

    const apiConfig = {
      method: baseConfig.method,
      baseURL: baseConfig.baseURL,
      api: baseConfig.api(
        query.project_id,
        query.wings_id,
        query.external_or_internal,
      ),
    };

    const { data } = await api(apiConfig);
    dispatch(saveFloors(extractList(data)));
    return data;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to load floors");
    dispatch(setError(message));
    Toast({ type: "error", message });
    throw error;
  } finally {
    dispatch(setSectionLoading({ key: "floors", value: false }));
    dispatch(setLoader(false));
  }
};

export const fetchUnits = (query) => async (dispatch) => {
  try {
    dispatch(setError(null));
    dispatch(setSectionLoading({ key: "units", value: true }));
    dispatch(setLoader(true));

    const baseConfig =
      MasterQuantityAddAPIs.getMasterQuantityFloorBasedUnitsApi;

    const apiConfig = {
      method: baseConfig.method,
      baseURL: baseConfig.baseURL,
      api: baseConfig.api(
        query.project_id,
        query.wings_id,
        query.external_or_internal,
        query.floor_id,
      ),
    };

    const { data } = await api(apiConfig);
    const sortedData = extractList(data).sort((a, b) => {
      // get last 2 digits only once
      const aNum = parseInt(a.flat_name.slice(-2), 10);
      const bNum = parseInt(b.flat_name.slice(-2), 10);

      return aNum - bNum;
    });

    dispatch(saveUnits(sortedData));
    return data;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to load units");
    dispatch(setError(message));
    Toast({ type: "error", message });
    throw error;
  } finally {
    dispatch(setSectionLoading({ key: "units", value: false }));
    dispatch(setLoader(false));
  }
};

export const fetchWorkLocations = (query) => async (dispatch) => {
  try {
    dispatch(setError(null));
    dispatch(setSectionLoading({ key: "workLocations", value: true }));
    dispatch(setLoader(true));

    const baseConfig = MasterQuantityAddAPIs.getWorkAreaOptionsApi;

    const apiConfig = {
      method: baseConfig.method,
      baseURL: baseConfig.baseURL,
      api: baseConfig.api(
        query.project_id,
        query.wing_id,
        query.floor_id,
        query.unit_id,
        query.external_or_internal,
      ),
    };

    const { data } = await api(apiConfig);
    dispatch(saveWorkLocations(extractList(data)));
    return data;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to load work locations");
    dispatch(setError(message));
    Toast({ type: "error", message });
    throw error;
  } finally {
    dispatch(setSectionLoading({ key: "workLocations", value: false }));
    dispatch(setLoader(false));
  }
};

export const fetchFinalDetails = (query) => async (dispatch) => {
  try {
    dispatch(setError(null));
    dispatch(setSectionLoading({ key: "finalDetails", value: true }));
    dispatch(setLoader(true));

    addQuery(query, MasterQuantityAddAPIs.getMasterQuantityFinalViewApi);

    const { data } = await api({
      ...MasterQuantityAddAPIs.getMasterQuantityFinalViewApi,
    });

    dispatch(saveFinalDetails(extractList(data)));
    return data;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to load final details");
    dispatch(setError(message));
    Toast({ type: "error", message });
    throw error;
  } finally {
    dispatch(setSectionLoading({ key: "finalDetails", value: false }));
    dispatch(setLoader(false));
  }
};

export const updateMasterQuantityAct = (payload, id) => async (dispatch) => {
  try {
    dispatch(setError(null));
    dispatch(setSectionLoading({ key: "updateQuantity", value: true }));
    dispatch(setLoader(true));

    const baseConfig = MasterQuantityAddAPIs.updateMasterQuantityApi;
    const apiConfig = {
      method: baseConfig.method,
      baseURL: baseConfig.baseURL,
      api: `${baseConfig.api}${id}`,
      body: payload, // if your helper expects body, change this to body
    };

    const { data } = await api(apiConfig);

    dispatch(updateFinalDetailsItemInState(payload));

    Toast({
      type: "success",
      message: data?.message || "Master quantity updated successfully",
    });

    return data;
  } catch (error) {
    const message = getErrorMessage(error, "Failed to update master quantity");
    dispatch(setError(message));
    Toast({ type: "error", message });
    throw error;
  } finally {
    dispatch(setSectionLoading({ key: "updateQuantity", value: false }));
    dispatch(setLoader(false));
  }
};
